/**
 * Blinkit-theme search (``/search``). Same state machine as the classic page (useSearchController): debounced
 * ``GET /search``, results sorted from the saved default address, Products / Shops tabs.
 *
 *   • Before typing — recent searches (this device only), popular categories, and the catalogue's popular items.
 *   • While typing  — skeleton tiles until the first results land, then a live product grid.
 *   • No match      — a clear empty state with suggestions to try instead.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, MapPin, Search, SearchX, X } from "lucide-react";
import { api } from "../../../lib/api";
import VoiceSearch from "../../../components/VoiceSearch";
import BizCategoryIcon from "../../../components/BizCategoryIcon";
import BkProductTile from "../../../components/blinkit/BkProductTile";
import BkStoreCard from "../../../components/blinkit/BkStoreCard";
import { BkChip, BkEmptyState, BkTileSkeletonGrid } from "../../../components/blinkit/BkUI";
import { useCartQuantities } from "../../../lib/useCartQuantities";
import { useSearchController } from "../controllers/useSearchController";

const RECENT_KEY = "bk_recent_searches";
const GRID = "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6";

function readRecent() {
    try {
        const v = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
        return Array.isArray(v) ? v.filter((x) => typeof x === "string").slice(0, 8) : [];
    } catch { return []; }
}
function writeRecent(list) {
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(list)); } catch { /* private mode — recents are optional */ }
}

export default function BkSearchView() {
    const s = useSearchController();
    const { q, input, setInput, loading, tab, setTab, counts, gatedProducts, gatedRestaurants, coords } = s;
    const nav = useNavigate();
    const cartMap = useCartQuantities();
    const inputRef = useRef(null);
    const [recent, setRecent] = useState(readRecent);
    const [verticals, setVerticals] = useState([]);

    // Voice search navigates to /search?q=… — reflect a new ?q= in the box even while already on this page.
    useEffect(() => { if (q) setInput(q); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        api.get("/business-categories").then(({ data }) => setVerticals((data || []).filter((v) => !v.parent_id))).catch(() => {});
    }, []);

    // Which query the results on screen answer. Every response (success or failure) replaces ``results``; the
    // request is debounced, so until a response lands for what's in the box we show skeletons — never a false
    // "no results".
    const inputNow = useRef(input);
    inputNow.current = input;
    const sawInitialResults = useRef(false);
    const [resolvedFor, setResolvedFor] = useState(null);
    useEffect(() => {
        if (!sawInitialResults.current) { sawInitialResults.current = true; return; }
        setResolvedFor(inputNow.current);
    }, [s.results]);

    const remember = (term) => {
        const t = (term || "").trim();
        if (!t) return;
        const next = [t, ...recent.filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(0, 8);
        setRecent(next);
        writeRecent(next);
    };
    const onSubmit = (e) => { remember(input); s.submit(e); inputRef.current?.blur(); };
    const pick = (term) => { setInput(term); remember(term); };
    const clearRecent = () => { setRecent([]); writeRecent([]); };

    const typing = input.trim().length > 0;
    const firstLoad = resolvedFor !== input;
    const popular = useMemo(() => gatedProducts.slice(0, 10), [gatedProducts]);

    return (
        <div className="pb-32 min-w-0" data-testid="bk-search-page">
            <header className="sticky top-0 z-30 bg-card border-b border-border" data-testid="bk-search-header">
                <div className="h-16 px-2 flex items-center gap-1.5">
                    <button type="button" onClick={() => (window.history.length > 1 ? nav(-1) : nav("/"))} aria-label="Go back"
                            className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center hover:bg-muted active:scale-95 transition" data-testid="bk-back">
                        <ArrowLeft size={21} />
                    </button>
                    <form onSubmit={onSubmit} className="flex-1 min-w-0 h-11 flex items-center gap-2 rounded-xl border border-border bg-muted/40 pl-3 pr-1 focus-within:border-primary focus-within:bg-card transition" role="search">
                        <Search size={18} className="shrink-0 text-muted-foreground" />
                        <input ref={inputRef} data-testid="search-input" autoFocus value={input} enterKeyHint="search"
                               onChange={(e) => setInput(e.target.value)} placeholder="Search for products or shops"
                               aria-label="Search" className="flex-1 min-w-0 bg-transparent outline-none text-[15px]" />
                        {typing && (
                            <button type="button" onClick={() => { setInput(""); inputRef.current?.focus(); }} aria-label="Clear search"
                                    className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted" data-testid="bk-search-clear">
                                <X size={16} />
                            </button>
                        )}
                        <VoiceSearch compact />
                    </form>
                </div>
                {typing && (
                    <div className="px-4 pb-2.5 flex items-center gap-2 overflow-x-auto no-scrollbar" data-testid="bk-search-tabs">
                        <BkChip active={tab === "products"} onClick={() => setTab("products")} testid="tab-products">
                            Products <span className="tabular-nums opacity-70">{counts.products}</span>
                        </BkChip>
                        <BkChip active={tab === "shops"} onClick={() => setTab("shops")} testid="tab-shops">
                            Shops <span className="tabular-nums opacity-70">{counts.shops}</span>
                        </BkChip>
                        {coords && (
                            <span className="ml-auto shrink-0 inline-flex items-center gap-1 text-[11px] text-muted-foreground font-semibold">
                                <MapPin size={11} /> Nearest first
                            </span>
                        )}
                    </div>
                )}
            </header>

            {!typing ? (
                <div className="px-4 pt-4 space-y-7">
                    {recent.length > 0 && (
                        <section data-testid="bk-recent">
                            <div className="flex items-center justify-between mb-2.5">
                                <h2 className="text-[15px] font-extrabold">Recent searches</h2>
                                <button type="button" onClick={clearRecent} className="text-[12px] font-bold text-primary" data-testid="bk-recent-clear">Clear</button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {recent.map((r) => (
                                    <BkChip key={r} onClick={() => pick(r)} testid={`bk-recent-${r}`}>
                                        <Clock size={13} className="text-muted-foreground" /> {r}
                                    </BkChip>
                                ))}
                            </div>
                        </section>
                    )}
                    {verticals.length > 0 && (
                        <section data-testid="bk-popular-cats">
                            <h2 className="text-[15px] font-extrabold mb-3">Shop by category</h2>
                            <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-x-2 gap-y-4">
                                {verticals.slice(0, 8).map((v) => (
                                    <Link key={v.id} to={`/c/${v.slug}/products`} data-testid={`bk-search-cat-${v.slug}`}
                                          className="flex flex-col items-center text-center gap-1.5 min-w-0 active:scale-95 transition">
                                        <span className="w-full aspect-square max-w-[76px] rounded-2xl bg-primary/[0.07] flex items-center justify-center text-primary">
                                            {v.icon_url ? <img src={v.icon_url} alt="" className="w-3/5 h-3/5 object-contain" /> : <BizCategoryIcon name={v.icon} size={26} />}
                                        </span>
                                        <span className="text-[11.5px] font-semibold leading-tight line-clamp-2">{v.name}</span>
                                    </Link>
                                ))}
                            </div>
                        </section>
                    )}
                    <section data-testid="bk-popular-products">
                        <h2 className="text-[15px] font-extrabold mb-3">Popular right now</h2>
                        {popular.length === 0 && loading ? (
                            <BkTileSkeletonGrid count={4} className={GRID} />
                        ) : popular.length === 0 ? (
                            <p className="text-[13px] text-muted-foreground">Start typing to find products and shops.</p>
                        ) : (
                            <div className={`grid gap-3 ${GRID}`}>
                                {popular.map((p) => <BkProductTile key={`${p.restaurant_id}-${p.id}`} product={p} qty={cartMap[p.id] || 0} testidPrefix="prod-hit" />)}
                            </div>
                        )}
                    </section>
                </div>
            ) : (
                <div className="px-4 pt-3">
                    {tab === "products" && (
                        firstLoad ? <BkTileSkeletonGrid count={6} className={GRID} testid="bk-search-loading" />
                        : gatedProducts.length > 0 ? (
                            <div data-testid="search-products" className={`grid gap-3 ${GRID}`}>
                                {gatedProducts.map((p) => (
                                    <div key={`${p.restaurant_id}-${p.id}`} onClickCapture={() => remember(input)} className="min-w-0 flex">
                                        <BkProductTile product={p} qty={cartMap[p.id] || 0} testidPrefix="prod-hit" />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <NoResults term={input} suggestions={[...recent, ...verticals.map((v) => v.name)].slice(0, 6)} onPick={pick} />
                        )
                    )}
                    {tab === "shops" && (
                        firstLoad ? <BkTileSkeletonGrid count={2} className="grid-cols-1 md:grid-cols-2" />
                        : gatedRestaurants.length > 0 ? (
                            <div data-testid="search-restaurants" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                                {gatedRestaurants.map((r) => <BkStoreCard key={r.id} r={r} />)}
                            </div>
                        ) : (
                            <NoResults term={input} kind="shops" suggestions={verticals.map((v) => v.name).slice(0, 6)} onPick={pick} />
                        )
                    )}
                    {loading && !firstLoad && (tab === "products" ? gatedProducts : gatedRestaurants).length > 0 && (
                        <p className="text-center text-[12px] text-muted-foreground py-3" role="status">Updating results…</p>
                    )}
                </div>
            )}
        </div>
    );
}

function NoResults({ term, kind = "products", suggestions, onPick }) {
    return (
        <BkEmptyState
            icon={SearchX}
            testid="bk-search-empty"
            title={`No ${kind} found for “${term}”`}
            body="Check the spelling or try a shorter, more general word."
            action={suggestions.length > 0 && (
                <div className="flex flex-wrap justify-center gap-2 max-w-sm">
                    {suggestions.map((t) => <BkChip key={t} onClick={() => onPick(t)}>{t}</BkChip>)}
                </div>
            )}
        />
    );
}
